import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { handleError } from "src/Common/function";
import PolicyService from "src/Services/API/policy.service";
import RoleService from "src/Services/API/role.service";

/**
 * Attach Policy modal — pick from policies not already attached to the role
 * and attach them (re-uses the role setPolicies endpoint with the merged set).
 */
function AttachPolicyModal({ shown, roleId, attachedPolicyIds, onClose, onSaved }) {
  const [allPolicies, setAllPolicies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (shown) {
      setSelected([]);
      setSearch("");
      PolicyService.getAll()
        .then((res) => setAllPolicies(res.data ?? []))
        .catch(handleError);
    }
  }, [shown]);

  const available = useMemo(() => {
    const attached = new Set(attachedPolicyIds ?? []);
    const q = search.trim().toLowerCase();
    return allPolicies
      .filter((p) => !attached.has(p.id))
      .filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [allPolicies, attachedPolicyIds, search]);

  if (!shown) return null;

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = () => {
    if (selected.length === 0) {
      onClose?.();
      return;
    }
    setIsSaving(true);
    const merged = Array.from(new Set([...(attachedPolicyIds ?? []), ...selected]));
    RoleService.setPolicies(roleId, merged)
      .then(() => {
        toast("Policies attached");
        onSaved?.();
        onClose?.();
      })
      .catch(handleError)
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-[12px] w-[520px] max-w-[92vw] p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[18px] font-OpenSansBold text-[#1A2332] mb-4">Attach Policy</h2>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search policies"
          className="w-full px-3 py-2 mb-4 rounded-[8px] border border-[#E5E8F3] text-[14px] font-OpenSansRegular outline-none focus:border-[#0058E5]"
        />

        <div className="max-h-[320px] overflow-y-auto flex flex-col gap-2 mb-5">
          {available.length === 0 ? (
            <p className="text-[13px] font-OpenSansRegular text-[#868DA6] py-6 text-center">
              No more policies to attach.
            </p>
          ) : (
            available.map((p) => (
              <label
                key={p.id}
                className="flex items-start gap-3 p-3 rounded-[8px] border border-[#E5E8F3] cursor-pointer hover:border-[#0058E5]"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(p.id)}
                  onChange={() => toggle(p.id)}
                  className="mt-1 accent-[#0058E5]"
                />
                <div>
                  <p className="text-[14px] font-OpenSansSemiBold text-[#1A2332]">{p.name}</p>
                  <p className="text-[12px] font-OpenSansRegular text-[#868DA6]">{p.description}</p>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[8px] border border-[#E5E8F3] text-[14px] font-OpenSansSemiBold text-[#404A6E] hover:bg-[#F8F9FE]"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={isSaving}
            className="px-4 py-2 rounded-[8px] bg-[#0058E5] text-white text-[14px] font-OpenSansSemiBold hover:bg-[#0048bd] disabled:opacity-60"
          >
            {isSaving ? "Attaching…" : `Attach${selected.length ? ` (${selected.length})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AttachPolicyModal;
